import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { RootState, useAppDispatch } from "../../redux/store";
import {
    Loader2,
    Clock,
    Calendar,
    FileText,
    CalendarX,
    CalendarClock,
    Info,
    Building2,
    MapPin,
} from "lucide-react";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { DateTimeUtil } from "../../utils";
import { NgwAvatar } from "../../components/common";
import { ApprovalHistory } from "../../components/notificationv2";
import { callPhpWorkflow } from "../../redux/features/common";
import { getNotificationDetail } from "../../redux/features/notifications";

const PENDING_STORAGE_KEY = "notification_detail_pending";

const UnavailabilityNotificationDetail: React.FC = () => {
    const notificationDetail = useSelector((state: RootState) => state.notification.notificationDetail);
    const user = useSelector((state: RootState) => state.user.user);
    const isLoading = useSelector((state: RootState) => state.notification.isNotificationDetailLoading);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navState = location.state as { notificationTransactionId?: string; employeeId?: number } | undefined;
    const [pendingFromStorage, setPendingFromStorage] = useState<{
        transactionId?: string;
        employeeId?: number;
    } | null>(() => {
        try {
            const raw = localStorage.getItem(PENDING_STORAGE_KEY);
            if (!raw) return null;
            const data = JSON.parse(raw) as { category?: string; transactionId?: string; employeeId?: number };
            return data?.transactionId != null
                ? { transactionId: data.transactionId, employeeId: data.employeeId }
                : null;
        } catch {
            return null;
        }
    });
    const transactionId = navState?.notificationTransactionId ?? pendingFromStorage?.transactionId;
    const employeeIdParam = navState?.employeeId ?? pendingFromStorage?.employeeId ?? undefined;
    const [notes, setNotes] = useState("");
    const isCallingWorkflow = useSelector((state: RootState) => state.common.isCallingWorkflow);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // When opened from SCH unavailability modal (state or localStorage for new window), fetch detail on this page
    useEffect(() => {
        if (!transactionId) return;
        const employeeId = employeeIdParam ?? user?.os_emp_account_id;
        if (!employeeId) return;
        dispatch(
            getNotificationDetail({
                notificationId: transactionId,
                employeeId: Number(employeeId),
            })
        );
        if (pendingFromStorage) {
            try {
                localStorage.removeItem(PENDING_STORAGE_KEY);
            } catch (_) {}
            setPendingFromStorage(null);
        }
    }, [transactionId, employeeIdParam, user?.os_emp_account_id, dispatch, pendingFromStorage]);

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Unavailability Request"));
    }, [dispatch]);

    // Extract unavailability details from notification
    const unavailabilityDetails = notificationDetail?.employee_unavailability_details;
    
    // Get requestor from unavailability details or workflow details
    const getRequestor = (): { employee_id?: string; name?: string; image_url?: string } | null => {
        if (unavailabilityDetails?.requestor) {
            return unavailabilityDetails.requestor;
        }
        const firstStep = notificationDetail?.workflow_details?.[0];
        if (firstStep?.actor_json) {
            const actorId = Object.keys(firstStep.actor_json)[0];
            return firstStep.actor_json[actorId];
        }
        return null;
    };

    const requestor = getRequestor();

    // Show loading when: Redux loading, or we landed with transaction id but detail not loaded yet
    const isPendingFromNav = Boolean(transactionId && !notificationDetail);
    if (isLoading || isPendingFromNav) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
            </div>
        );
    }

    const getCurrentStep = () => {
        const currentStepCode = notificationDetail?.workflow_current_step_details?.step_code;
        return notificationDetail?.workflow_details?.filter((wf) => wf.step_code === currentStepCode)?.[0];
    };

    const handleApprove = async () => {
        const unavailabilityId = unavailabilityDetails?.employee_unavailability_slot_id || notificationDetail?.alert_json?.REF_ID?.toString();
        const employeeId = requestor?.employee_id;

        if (!unavailabilityId || !employeeId) return;

        const body = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 1,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: unavailabilityId,
            result: { APPROVED: { [employeeId]: [unavailabilityId] }, REJECTED: {} },
            notes,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();
    };

    const handleReject = async () => {
        const unavailabilityId = unavailabilityDetails?.employee_unavailability_slot_id || notificationDetail?.alert_json?.REF_ID?.toString();
        const employeeId = requestor?.employee_id;

        if (!unavailabilityId || !employeeId) return;

        const body = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 0,
            user_account_id: user?.userAccountId?.toString() ?? "",
            actor_id: user?.id?.toString() ?? "",
            reference_id: unavailabilityId,
            result: { APPROVED: {}, REJECTED: { [employeeId]: [unavailabilityId] } },
            notes,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
        };

        await dispatch(callPhpWorkflow(body));
        refreshData();
    };

    const refreshData = async () => {
        setIsRefreshing(true);
        await dispatch(
            getNotificationDetail({
                notificationId: notificationDetail?.notification_id!,
                employeeId: Number(user.os_emp_account_id!),
            })
        );
        setIsRefreshing(false);
    };

    // Helper to format date display using dayjs
    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return DateTimeUtil.formatWithDayjs(dateString, "MMM DD, YYYY");
    };

    // Helper to format time display
    const formatTime = (timeString?: string) => {
        if (!timeString) return "N/A";
        return DateTimeUtil.getFormattedTime(timeString, "N/A");
    };

    // Helper to get formatted date range (without time)
    const getFormattedDateRange = () => {
        if (!unavailabilityDetails?.start_date || !unavailabilityDetails?.end_date) return null;
        
        const formattedStart = DateTimeUtil.formatWithDayjs(unavailabilityDetails.start_date, "MMM D, YYYY");
        const formattedEnd = DateTimeUtil.formatWithDayjs(unavailabilityDetails.end_date, "MMM D, YYYY");
        
        // If start and end dates are the same, return only start date
        if (unavailabilityDetails.start_date === unavailabilityDetails.end_date) {
            return formattedStart;
        }
        
        return `${formattedStart} - ${formattedEnd}`;
    };

    // Helper to check if start and end dates are the same
    const isSingleDate = () => {
        return unavailabilityDetails?.start_date === unavailabilityDetails?.end_date;
    };

    // Helper to get formatted time range
    const getFormattedTimeRange = () => {
        if (!unavailabilityDetails?.from_time || !unavailabilityDetails?.to_time) return null;
        
        // Add a date to the time to make it a valid datetime for parsing
        const formattedFrom = DateTimeUtil.getFormattedTime(`2000-01-01 ${unavailabilityDetails.from_time}`, "N/A");
        const formattedTo = DateTimeUtil.getFormattedTime(`2000-01-01 ${unavailabilityDetails.to_time}`, "N/A");
        
        return `${formattedFrom} - ${formattedTo}`;
    };

    // Helper to expand abbreviated day names to full names
    const expandDayName = (abbreviation: string): string => {
        const dayMap: { [key: string]: string } = {
            'mon': 'Monday',
            'tue': 'Tuesday',
            'tues': 'Tuesday',
            'wed': 'Wednesday',
            'thu': 'Thursday',
            'thur': 'Thursday',
            'thurs': 'Thursday',
            'fri': 'Friday',
            'sat': 'Saturday',
            'sun': 'Sunday'
        };
        return dayMap[abbreviation.toLowerCase()] || abbreviation;
    };

    // Helper to format repeat days
    const getFormattedRepeatDays = () => {
        if (!unavailabilityDetails?.repeat_days) return null;
        const days = unavailabilityDetails.repeat_days.split(',').map(day => day.trim());
        const expandedDays = days.map(day => expandDayName(day));
        return expandedDays.join(', ');
    };

    // Helper to get department limitations
    const getDepartmentLimitations = () => {
        if (!unavailabilityDetails?.limitation_json?.DEPARTMENT) return null;
        const departments = unavailabilityDetails.limitation_json.DEPARTMENT;
        return departments.length > 0 ? departments.join(", ") : null;
    };

    // Helper to get site limitations
    const getSiteLimitations = () => {
        if (!unavailabilityDetails?.limitation_json?.SITE) return null;
        const sites = unavailabilityDetails.limitation_json.SITE;
        return sites.length > 0 ? sites.join(", ") : null;
    };

    // Helper to check if it's all day (both times are null)
    const isAllDay = () => {
        return unavailabilityDetails?.from_time === null && unavailabilityDetails?.to_time === null;
    };

    return (
        <div className="bg-gray-50 min-h-screen pb-8">
            <div className="w-full md:w-1/2 mx-auto">
                {/* Employee Info Card */}
                <div className="mx-4 mt-4">
                    <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-blue-500">
                        <div className="flex items-start space-x-3">
                            <div className="w-12 h-12 relative">
                                <NgwAvatar src={requestor?.image_url} name={requestor?.name} />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                        REQUESTOR
                                    </span>
                                </div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                    {requestor?.name || "Employee Name"}
                                </h2>
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    {notificationDetail?.message_info?.body || "Submitted unavailability request"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Unavailability Details Card */}
                <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3 mt-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                        <CalendarX className="w-5 h-5 mr-2 text-gray-600" />
                        Unavailability Request Details
                    </h3>

                    {unavailabilityDetails ? (
                        <div className="space-y-3">
                            {/* Date Range */}
                            {getFormattedDateRange() && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Calendar size={16} className="flex-shrink-0" />
                                    <span className="text-sm">
                                        <span className="font-medium">{isSingleDate() ? "Date:" : "Dates:"}</span>{" "}
                                        {getFormattedDateRange()}
                                    </span>
                                </div>
                            )}

                            {/* Time Range */}
                            {getFormattedTimeRange() && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Clock size={16} className="flex-shrink-0" />
                                    <span className="text-sm">
                                        <span className="font-medium">Times:</span>{" "}
                                        {getFormattedTimeRange()}
                                    </span>
                                </div>
                            )}

                            {/* Show All Day only if all_day is "1" */}
                            {unavailabilityDetails.all_day === "1" && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <CalendarClock size={16} className="flex-shrink-0" />
                                    <span className="text-sm font-medium">All Day</span>
                                </div>
                            )}

                            {/* Repeating Days */}
                            {unavailabilityDetails.repeating === "1" && getFormattedRepeatDays() && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <CalendarClock size={16} className="flex-shrink-0" />
                                    <span className="text-sm">
                                        <span className="font-medium">Days:</span> Every {getFormattedRepeatDays()}
                                    </span>
                                </div>
                            )}

                            {/* Department Limitations */}
                            {getDepartmentLimitations() && (
                                <div className="flex items-start gap-2 text-gray-600">
                                    <Building2 size={16} className="mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{getDepartmentLimitations()}</span>
                                </div>
                            )}

                            {/* Site Limitations */}
                            {getSiteLimitations() && (
                                <div className="flex items-start gap-2 text-gray-600">
                                    <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{getSiteLimitations()}</span>
                                </div>
                            )}

                            {/* Note */}
                            {unavailabilityDetails.note && (
                                <div className="flex items-start gap-2 text-gray-600">
                                    <FileText size={16} className="mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">
                                        <span className="font-medium">Note:</span> {unavailabilityDetails.note}
                                    </span>
                                </div>
                            )}

                            {/* Status */}
                            {unavailabilityDetails.status && (
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                        unavailabilityDetails.status.toLowerCase() === 'pending' 
                                            ? 'text-orange-700 bg-orange-100' 
                                            : unavailabilityDetails.status.toLowerCase() === 'approved'
                                            ? 'text-green-700 bg-green-100'
                                            : unavailabilityDetails.status.toLowerCase() === 'rejected'
                                            ? 'text-red-700 bg-red-100'
                                            : 'text-gray-700 bg-gray-100'
                                    }`}>
                                        {unavailabilityDetails.status.toUpperCase()}
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-sm text-gray-600 py-2">
                            <p>Detailed unavailability information is not available at this time.</p>
                        </div>
                    )}

                    {/* Approval History Section */}
                    <ApprovalHistory workflowDetails={notificationDetail?.workflow_details} title="Approval History" />

                    {/* Add note textbox */}
                    {getCurrentStep() &&
                        getCurrentStep()?.current_status == "0" &&
                        getCurrentStep()?.actor_json?.[user.id!] &&
                        notificationDetail?.only_notify_no_action !== "1" && (
                            <div className="mt-4">
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full border-2 rounded-md p-2"
                                    placeholder="Add notes"
                                />
                            </div>
                        )}

                    {getCurrentStep() &&
                        getCurrentStep()?.current_status == "0" &&
                        getCurrentStep()?.actor_json?.[user.id!] &&
                        notificationDetail?.only_notify_no_action !== "1" && (
                            <div>
                                {isCallingWorkflow ? (
                                    <Loader2 className="animate-spin text-blue-500" />
                                ) : (
                                    <div className="flex space-x-2 mt-4">
                                        <button
                                            onClick={handleApprove}
                                            className="flex-1 bg-white border border-blue-600 hover:bg-blue-50 text-blue-600 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                        >
                                            Approve
                                        </button>
                                        <button
                                            onClick={handleReject}
                                            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-3 rounded-md transition-colors text-sm"
                                        >
                                            Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

export default UnavailabilityNotificationDetail;

